import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';

export type StickyProgressBarCtaVariant = 'main' | 'white' | 'black';

/**
 * Shared bottom sticky bar: N-step breadcrumb + a free `<ng-content>` slot for
 * page-specific totals + a single CTA button.
 *
 * Purely presentational — reused, as-is, by seat-selection, checkout and summary
 * (gs-portal-redesign plan, V5 / DEC-010): it always stays fixed to the bottom of
 * the viewport in every view that renders it, on both desktop and mobile.
 *
 * GS-local component today (DEC-001): no cart-specific data lives here, so it has
 * no reason to depend on cart internals — it is a plausible future SDC candidate
 * (see plan section 3) if another app needs the same breadcrumb+CTA footer.
 */
@Component({
  selector: 'app-sticky-progress-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sticky-progress-bar.component.html',
  styleUrl: './sticky-progress-bar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StickyProgressBarComponent {
  /** 1-indexed step currently active. */
  currentStep = input.required<number>();
  steps = input<string[]>(['Select your new Seats', 'Checkout', 'Resume']);

  ctaLabel = input.required<string>();
  ctaDisabled = input(false);
  ctaVariant = input<StickyProgressBarCtaVariant>('main');
  ctaTooltip = input<string>('');
  ctaDataCy = input<string>('sticky-progress-bar-cta');
  /**
   * Whether to render the lock icon next to the CTA label (Figma `1683:17`,
   * "Submit Payment" + `Icon ion-md-lock`). This bar is shared by seat-selection
   * ("Go to Checkout"), checkout ("Checkout" → submits payment) and summary
   * ("Back to Home") — a padlock only makes sense for the payment-submitting
   * state, so it's an explicit opt-in input rather than inferred from
   * `ctaLabel()` text, which would silently break if that copy ever changes.
   */
  ctaShowLockIcon = input(false);

  ctaClick = output<void>();

  get ctaClasses(): string {
    switch (this.ctaVariant()) {
      case 'white':
        return 'btn-white';
      case 'black':
        return 'btn-black';
      default:
        return 'btn-main';
    }
  }

  onCtaClick(): void {
    if (this.ctaDisabled()) {
      return;
    }
    this.ctaClick.emit();
  }
}
